import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { LegacyPage } from '@jira/router-routes__common/src/ui/legacy-page/main.tsx';
import type {
	ApplicationLinkManifest,
	OutboundConnection,
} from '@jira/applinks__administration/src/CommonApplicationLinkCreationEntity.tsx';
import CreateOAuth2Drawer from '@jira/applinks__administration/src/CreateOAuth2Drawer.tsx';
import type { RouteContext } from '@atlassian/react-resource-router';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { fg } from '@atlassian/jira-feature-gating';

/**
 * Add `initiateApplinksOAuth2Creation`, `refreshApplinksInitiatedFromReact`, and
 * `AppLinks.Creation.Dialogs.doOAuth2FinalizationInitiatedFromReact` to the global window object.
 *
 * Is this gross? Yes.
 * Is it fine? Also yes, because the legacy applinks frontend extensively stores things in `window`.
 *
 * Read on for an explanation of how the two systems interact.
 *
 * ## Legacy -> Modern:
 * In the legacy frontend, we invoke `window.initiateApplinksOAuth2Creation(url)` from the old
 * dialogue to delegate to the new React based dialogue.
 *
 *
 * ## Modern -> Legacy
 * Once we have created the Applink, we can call `doOAuth2FinalizationInitiatedFromReact` to reset the creation
 * state of the original page. This doesn't do a whole lot - it resets the button and clears the state.
 *
 * Following this, we can optionally invoke `refreshApplinksInitiatedFromReact` to update the legacy list view. This
 * prevents awkward full-page refreshes when using the modal.
 *
 */
declare global {
	interface Window {
		initiateApplinksOAuth2Creation?: (targetUrl: string) => void;
		refreshApplinksInitiatedFromReact?: () => void;
		AppLinks: {
			Creation: {
				getCreationState: () => {
					localApplication?: ApplicationLinkManifest;
					remoteApplication?: ApplicationLinkManifest;
				};
				Process: {
					doOAuth2FinalizationInitiatedFromReact?: () => void;
					outboundConnections?: OutboundConnection[];
					onAppLinkOAuth2CreationReady?: () => void;
				};
			};
		};
	}
}

type Props = RouteContext;

const PARENT_ELEMENT_SELECTOR = '.applinks-tunnel-header';
const PORTAL_ELEMENT_KEY = 'react-applinks-button';

export const LOG_LOCATION = 'applinks.administration.legacy';

const refreshLegacyPageState = () => {
	globalThis.window.refreshApplinksInitiatedFromReact?.();
	globalThis.window.AppLinks.Creation.Process.doOAuth2FinalizationInitiatedFromReact?.();
};

/**
 * React portal containing the drawer.
 */
const PortalledElement = () => {
	const [drawerContext, setDrawerContext] = useState<string | null>(null);
	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

	const openOAuth2Drawer = useCallback((targetUrl: string) => {
		setDrawerContext(targetUrl);
		setIsDrawerOpen(true);
	}, []);
	const closeOAuth2Drawer = useCallback(() => {
		setIsDrawerOpen(false);
	}, []);

	useEffect(() => {
		globalThis.window.initiateApplinksOAuth2Creation = openOAuth2Drawer;
		globalThis.window.AppLinks.Creation.Process.onAppLinkOAuth2CreationReady?.();
	}, [openOAuth2Drawer]);

	const availableOutboundConnections =
		globalThis.window.AppLinks?.Creation?.Process?.outboundConnections;

	const { localApplication, remoteApplication } = fg(
		'cloud-connector-applinks-migration-m1-jira-srs',
	)
		? // oxlint-disable-next-line no-unsafe-optional-chaining
			globalThis.window.AppLinks?.Creation?.getCreationState()
		: {};

	return (
		<>
			{drawerContext !== null && (
				<CreateOAuth2Drawer
					{...(fg('cloud-connector-applinks-migration-m1-jira-srs') && { key: drawerContext })} // clear state when creating for a different target URL
					isOpen={isDrawerOpen}
					onClose={closeOAuth2Drawer}
					onCloseComplete={refreshLegacyPageState}
					targetUrl={drawerContext}
					availableConnections={availableOutboundConnections}
					localManifest={localApplication}
					remoteManifest={remoteApplication}
				/>
			)}
		</>
	);
};

/**
 * Creates a portal in the Applinks header (i.e. where the "Create link" button is)
 */
const Portal = (_: {}) => {
	const linkElement = globalThis?.document.querySelector<HTMLElement>(PARENT_ELEMENT_SELECTOR);
	if (linkElement) {
		return createPortal(<PortalledElement />, linkElement, PORTAL_ELEMENT_KEY);
	}

	log.safeErrorWithoutCustomerData(
		LOG_LOCATION,
		`No link element found in the DOM (with selector \`${PARENT_ELEMENT_SELECTOR}\`)`,
		undefined,
	);
	return null;
};

/**
 * Inject React into the Legacy frontend
 */
const ModernApplinksCreateDrawer = (props: Props): React.JSX.Element => {
	return (
		<>
			<LegacyPage {...props} />
			<Portal />
		</>
	);
};

export default ModernApplinksCreateDrawer;
